import React, { useState, useEffect } from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Radio,
  Select,
  TimePicker,
} from 'antd';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import { SectionTitle } from 'ui/SectionTitle';
import { requiredRule } from 'utils/forms/validationRules';
import { SegmentsService } from 'services/SegmentsService';
import { sendingFrequencyList } from 'utils/handbooks/pushManagerHandbooks';
import { formatDate } from 'utils';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { PushNotificationService } from 'services/PushNotificationService';
import dayjs from 'dayjs';

function PushNotificationForm({ initialValues, isActive, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [segments, setSegments] = useState([]);
  const [frequencyType, setFrequencyType] = useState(null);
  const [period, setPeriod] = useState(null);
  const isEditing = initialValues !== null;
  const hiddenTab = initialValues?.type;

  useEffect(() => {
    if (!initialValues) {
      resetState();
    } else {
      prePopulateForm();
    }
  }, [initialValues]);

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      const items = await SegmentsService.getSegmentsSelect();
      setSegments(items);
    } catch (err) {
      message.error('Не удалось загрузить сегменты');
    }
  };

  const prePopulateForm = () => {
    const {
      title,
      header,
      text,
      url,
      segments,
      type,
      send_frequency,
      date_send,
      time_send,
      active_until,
    } = initialValues;

    const [hours, minutes, seconds] = time_send.split(':').map(Number);

    const formValues = {
      title,
      header,
      text,
      url,
      type,
      segment_ids: segments.map(({ id }) => id),
      date_send: dayjs(date_send),
      time_send: dayjs({ hours, minutes, seconds }),
    };

    if (type === 'periodical') {
      setPeriod(send_frequency);
      form.setFieldsValue({
        send_frequency,
        active_until: dayjs(active_until),
      });
    }

    setFrequencyType(type);
    form.setFieldsValue(formValues);
  };

  const normolizePayload = (values) => {
    const data = { ...values };

    if (frequencyType === 'periodical') {
      data.active_until = formatDate(data.active_until, 'YYYY-MM-DD');
    }

    if (period !== 'daily') {
      data.date_send = formatDate(data.date_send, 'YYYY-MM-DD');
    }

    data.time_send = formatDate(data.time_send, 'HH:mm:ss');

    return data;
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await PushNotificationService.createPushNotification(
        data
      );

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await PushNotificationService.updatePushNotification(
        id,
        data
      );

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleSubmit = (values) => {
    const payload = normolizePayload(values);

    if (isEditing) {
      handleUpdate(payload, initialValues.id);
    } else {
      handleCreate(payload);
    }
  };

  const handleConfirm = () => {
    onSuccess();
    onClose();
  };

  const resetState = () => {
    form.resetFields();
    setFrequencyType(null);
    setFrequencyType(null);
    setPeriod(null);
  };

  return (
    <Drawer
      title={
        initialValues
          ? 'Редактирование PUSH уведомление'
          : 'Создание PUSH уведомление'
      }
      width="50%"
      onClose={onClose}
      visible={isActive}
    >
      <Form
        form={form}
        layout="horizontal"
        labelAlign="right"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        onFinish={handleSubmit}
      >
        <SectionTitle>Уведомление</SectionTitle>

        <Form.Item
          name="title"
          label="Название уведомлениe"
          rules={[requiredRule]}
        >
          <Input allowClear placeholder="Введите название" />
        </Form.Item>

        <Form.Item
          name="header"
          label="Заголовок уведомлениe"
          rules={[requiredRule]}
        >
          <Input
            showCount
            maxLength={30}
            allowClear
            placeholder="Введите заголовок"
          />
        </Form.Item>

        <Form.Item name="text" label="Текст уведомлениe" rules={[requiredRule]}>
          <Input.TextArea
            showCount
            maxLength={120}
            allowClear
            placeholder="Введите текст"
            rows={3}
          />
        </Form.Item>

        <Form.Item name="url" label="Ссылка на страницу">
          <Input
            allowClear
            addonBefore="URL"
            placeholder="Введите cсылка на страницу"
          />
        </Form.Item>

        <SectionTitle>Аудитория</SectionTitle>

        <Form.Item name="segment_ids" label="Сегмент" rules={[requiredRule]}>
          <Select allowClear placeholder="Выберите cегмент" mode="multiple">
            {segments.map(({ title, id }) => (
              <Select.Option key={id} value={id}>
                {title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <SectionTitle>Время</SectionTitle>

        <Form.Item name="type" label="Частота" rules={[requiredRule]}>
          <Radio.Group onChange={(e) => setFrequencyType(e.target.value)}>
            {hiddenTab !== 'periodical' && (
              <Radio.Button value="one_time">Разовое</Radio.Button>
            )}

            {hiddenTab !== 'one_time' && (
              <Radio.Button value="periodical">Периодичная</Radio.Button>
            )}
          </Radio.Group>
        </Form.Item>

        {frequencyType === 'one_time' && (
          <>
            <Form.Item
              name="date_send"
              label="Дата отправки"
              rules={[requiredRule]}
            >
              <DatePicker
                format="DD.MM.YYYY"
                allowClear
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name="time_send"
              label="Время отправки"
              rules={[requiredRule]}
            >
              <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
            </Form.Item>
          </>
        )}

        {frequencyType === 'periodical' && (
          <>
            <Form.Item
              name="send_frequency"
              label="Частота отправки"
              rules={[requiredRule]}
            >
              <Select
                allowClear
                placeholder="Выберите частоту"
                disabled={initialValues}
                onChange={(value) => setPeriod(value)}
              >
                {sendingFrequencyList.map(({ text, value }) => (
                  <Select.Option key={value} value={value}>
                    {text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {period !== 'daily' && (
              <Form.Item
                name="date_send"
                label="Дата отправки"
                rules={[requiredRule]}
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  picker={period === 'weekly' ? 'week' : 'date'}
                  placeholder={
                    period === 'weekly'
                      ? 'Выберите день недели'
                      : 'Выберите дату'
                  }
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}

            <Form.Item
              name="time_send"
              label="Время отправки"
              rules={[requiredRule]}
            >
              <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="active_until"
              label="Дата окончание"
              rules={[requiredRule]}
            >
              <DatePicker
                format="DD.MM.YYYY"
                allowClear
                style={{ width: '100%' }}
              />
            </Form.Item>
          </>
        )}

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { PushNotificationForm };
